import React, { useState, useEffect } from "react";
import Header from "../../components/header/Header";
import Footer from "../../components/footer/Footer";
import "./CreateRetailAgent.css";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API_URL } from "../../constant";
import { FaEye, FaEyeSlash, FaCamera, FaTimes } from "react-icons/fa";
import ReCAPTCHA from "react-google-recaptcha";

const CreateRetailAgent = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [captchaToken, setCaptchaToken] = useState(null); // recaptcha

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    country: "United Kingdom",
    region: "England - City of London",
    address_line1: "",
    address_line2: "",
    address_line3: "",
    city: "",
    postcode: "", // ADDED
    password: "",
    password_confirmation: "",
    showPassword: false,
    showConfirmPassword: false,
  });

  // captcha handler
  const onCaptchaChange = (token) => {
    setCaptchaToken(token);
  };

  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoError, setPhotoError] = useState("");

  const [errors, setErrors] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    password: "",
    password_confirmation: "",
    address_line1: "",
    city: "",
    postcode: "", // ADDED
    photo: "",
  });

  const [message, setMessage] = useState({ text: "", type: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [agreedToRetailAgreement, setAgreedToRetailAgreement] = useState(false);
  const [touched, setTouched] = useState({
    first_name: false,
    last_name: false,
    email: false,
    phone_number: false,
    password: false,
    password_confirmation: false,
    address_line1: false,
    city: false,
    postcode: false, // ADDED
  });

  // Get referral code from URL parameter and persist it
  const [referralCode, setReferralCode] = useState(null);

  // Initialize referral code from URL or localStorage on component mount
  useEffect(() => {
    // First try to get from URL
    const urlRef = searchParams.get("ref");
    const urlCountry = searchParams.get("country");

    if (urlCountry && regionOptions[urlCountry]) {
      setFormData((prev) => ({
        ...prev,
        country: urlCountry,
        region: regionOptions[urlCountry]?.[0] || prev.region,
      }));
    }

    if (urlRef) {
      // Store in localStorage for persistence
      localStorage.setItem("agent_referral_code", urlRef);
      setReferralCode(urlRef);
      console.log("Referral code from URL:", urlRef);
    } else {
      // Check if we have a stored referral code
      const storedRef = localStorage.getItem("agent_referral_code");
      if (storedRef) {
        setReferralCode(storedRef);
        console.log("Referral code from localStorage:", storedRef);
      }
    }
  }, [searchParams]);

  // Clear referral code when component unmounts (optional)
  // useEffect(() => {
  //   return () => {
  //     // Only clear if navigating away from signup flow
  //     // You might want to keep it for the session
  //     // localStorage.removeItem('agent_referral_code');
  //   };
  // }, []);

  // Container styles
  const containerStyle = {
    minHeight: "100vh",
    fontFamily: "Arial, sans-serif",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    marginTop: "30px",
  };

  const formWrapperStyle = {
    display: "flex",
    justifyContent: "center",
    padding: "0px",
    flex: 1,
    boxSizing: "border-box",
    marginTop: "120px",
    width: "100%",
    marginBottom: "120px",
  };

  const regionOptions = {
    // Africa
    Afghanistan: ["Asia"],
    Albania: ["Europe"],
    Algeria: ["Africa"],
    Andorra: ["Europe"],
    Angola: ["Africa"],
    Argentina: ["South America"],
    Armenia: ["Asia"],
    Australia: ["Oceania"],
    Austria: ["Europe"],
    Azerbaijan: ["Asia"],
    Bahamas: ["North America"],
    Bahrain: ["Asia"],
    Bangladesh: ["Asia"],
    Barbados: ["North America"],
    Belarus: ["Europe"],
    Belgium: ["Europe"],
    Belize: ["North America"],
    Benin: ["Africa"],
    Bhutan: ["Asia"],
    Bolivia: ["South America"],
    "Bosnia and Herzegovina": ["Europe"],
    Botswana: ["Africa"],
    Brazil: ["South America"],
    Brunei: ["Asia"],
    Bulgaria: ["Europe"],
    "Burkina Faso": ["Africa"],
    Burundi: ["Africa"],
    Cambodia: ["Asia"],
    Cameroon: ["Africa"],
    Canada: ["Alberta", "British Columbia", "Manitoba", "Ontario", "Quebec"],
    "Cape Verde": ["Africa"],
    "Central African Republic": ["Africa"],
    Chad: ["Africa"],
    Chile: ["South America"],
    China: ["Asia"],
    Colombia: ["South America"],
    Comoros: ["Africa"],
    Congo: ["Africa"],
    "Costa Rica": ["North America"],
    Croatia: ["Europe"],
    Cuba: ["North America"],
    Cyprus: ["Europe"],
    "Czech Republic": ["Europe"],
    Denmark: ["Europe"],
    Djibouti: ["Africa"],
    "Dominican Republic": ["North America"],
    Ecuador: ["South America"],
    Egypt: ["Africa"],
    "El Salvador": ["North America"],
    Estonia: ["Europe"],
    Ethiopia: ["Africa"],
    Finland: ["Europe"],
    France: ["Europe"],
    Germany: ["Europe"],
    Ghana: ["Africa"],
    Greece: ["Europe"],
    Guatemala: ["North America"],
    Haiti: ["North America"],
    Honduras: ["North America"],
    Hungary: ["Europe"],
    Iceland: ["Europe"],
    India: [
      "Andhra Pradesh",
      "Delhi",
      "Karnataka",
      "Maharashtra",
      "Tamil Nadu",
      "West Bengal",
    ],
    Indonesia: ["Asia"],
    Iran: ["Tehran", "Isfahan", "Fars", "Mazandaran", "Khorasan Razavi"],
    Iraq: ["Asia"],
    Ireland: ["Europe"],
    Israel: ["Asia"],
    Italy: ["Europe"],
    Jamaica: ["North America"],
    Japan: ["Asia"],
    Jordan: ["Asia"],
    Kazakhstan: ["Asia"],
    Kenya: ["Africa"],
    Kuwait: ["Asia"],
    Kyrgyzstan: ["Asia"],
    Laos: ["Asia"],
    Latvia: ["Europe"],
    Lebanon: ["Asia"],
    Lesotho: ["Africa"],
    Liberia: ["Africa"],
    Libya: ["Africa"],
    Lithuania: ["Europe"],
    Luxembourg: ["Europe"],
    Madagascar: ["Africa"],
    Malawi: ["Africa"],
    Malaysia: ["Asia"],
    Maldives: ["Asia"],
    Mali: ["Africa"],
    Malta: ["Europe"],
    Mexico: ["North America"],
    Moldova: ["Europe"],
    Mongolia: ["Asia"],
    Morocco: ["Africa"],
    Mozambique: ["Africa"],
    Myanmar: ["Asia"],
    Namibia: ["Africa"],
    Nepal: ["Asia"],
    Netherlands: ["Europe"],
    "New Zealand": ["Oceania"],
    Nicaragua: ["North America"],
    Niger: ["Africa"],
    Nigeria: ["Africa"],
    "North Korea": ["Asia"],
    Norway: ["Europe"],
    Oman: ["Asia"],
    Pakistan: ["Asia"],
    Panama: ["North America"],
    Paraguay: ["South America"],
    Peru: ["South America"],
    Philippines: ["Asia"],
    Poland: ["Europe"],
    Portugal: ["Europe"],
    Qatar: ["Asia"],
    Romania: ["Europe"],
    Russia: ["Europe"],
    Rwanda: ["Africa"],
    "Saudi Arabia": ["Asia"],
    Senegal: ["Africa"],
    Serbia: ["Europe"],
    Singapore: ["Asia"],
    Slovakia: ["Europe"],
    Slovenia: ["Europe"],
    Somalia: ["Africa"],
    "South Africa": ["Africa"],
    "South Korea": ["Asia"],
    Spain: ["Europe"],
    "Sri Lanka": [
      "Central Province",
      "Eastern Province",
      "Northern Province",
      "Southern Province",
      "Western Province",
      "North Central Province",
      "North Western Province",
      "Sabaragamuwa Province",
      "Uva Province",
    ],
    Sudan: ["Africa"],
    Sweden: ["Europe"],
    Switzerland: ["Europe"],
    Syria: ["Asia"],
    Taiwan: ["Asia"],
    Tanzania: ["Africa"],
    Thailand: ["Asia"],
    Tunisia: ["Africa"],
    Turkey: ["Asia"],
    Uganda: ["Africa"],
    Ukraine: ["Europe"],
    "United Arab Emirates": ["Asia"],
    "United Kingdom": [
      "England - Bedfordshire",
      "England - Berkshire",
      "England - Bristol",
      "England - Buckinghamshire",
      "England - Cambridgeshire",
      "England - Cheshire",
      "England - City of London",
      "England - Cornwall",
      "England - Cumbria",
      "England - Derbyshire",
      "England - Devon",
      "England - Dorset",
      "England - Durham",
      "England - East Riding of Yorkshire",
      "England - East Sussex",
      "England - Essex",
      "England - Gloucestershire",
      "England - Greater London",
      "England - Greater Manchester",
      "England - Hampshire",
      "England - Herefordshire",
      "England - Hertfordshire",
      "England - Isle of Wight",
      "England - Kent",
      "England - Lancashire",
      "England - Leicestershire",
      "England - Lincolnshire",
      "England - Merseyside",
      "England - Norfolk",
      "England - North Yorkshire",
      "England - Northamptonshire",
      "England - Northumberland",
      "England - Nottinghamshire",
      "England - Oxfordshire",
      "England - Rutland",
      "England - Shropshire",
      "England - Somerset",
      "England - South Yorkshire",
      "England - Staffordshire",
      "England - Suffolk",
      "England - Surrey",
      "England - Sussex",
      "England - Tyne & Wear",
      "England - Warwickshire",
      "England - West Midlands",
      "England - West Sussex",
      "England - West Yorkshire",
      "England - Wiltshire",
      "England - Worcestershire",
      "Northern Ireland - Antrim",
      "Northern Ireland - Armagh",
      "Northern Ireland - Down",
      "Northern Ireland - Fermanagh",
      "Northern Ireland - Londonderry",
      "Northern Ireland - Tyrone",
      "Scotland - City of Aberdeen",
      "Scotland - Aberdeenshire",
      "Scotland - Angus",
      "Scotland - Argyll",
      "Scotland - Ayrshire",
      "Scotland - Banffshire",
      "Scotland - Berwickshire",
      "Scotland - Bute",
      "Scotland - Caithness",
      "Scotland - Clackmannanshire",
      "Scotland - Dumfriesshire",
      "Scotland - Dunbartonshire",
      "Scotland - City of Dundee",
      "Scotland - East Lothian",
      "Scotland - City of Edinburgh",
      "Scotland - Fife",
      "Scotland - City of Glasgow",
      "Scotland - Inverness-shire",
      "Scotland - Kincardineshire",
      "Scotland - Kinross-shire",
      "Scotland - Kirkcudbrightshire",
      "Scotland - Lanarkshire",
      "Scotland - Midlothian",
      "Scotland - Moray",
      "Scotland - Nairnshire",
      "Scotland - Orkney",
      "Scotland - Peeblesshire",
      "Scotland - Perthshire",
      "Scotland - Renfrewshire",
      "Scotland - Ross and Cromarty",
      "Scotland - Roxburghshire",
      "Scotland - Selkirkshire",
      "Scotland - Shetland",
      "Scotland - Stirlingshire",
      "Scotland - Sutherland",
      "Scotland - West Lothian",
      "Scotland - Wigtownshire",
      "Wales - Blaenau Gwent",
      "Wales - Bridgend",
      "Wales - Caerphilly",
      "Wales - Carmarthenshire",
      "Wales - Ceredigion",
      "Wales - Swansea",
      "Wales - Cardiff",
      "Wales - Conwy",
      "Wales - Denbighshire",
      "Wales - Flintshire",
      "Wales - Gwynedd",
      "Wales - Isle of Anglesey",
      "Wales - Merthyr Tydfil",
      "Wales - Monmouthshire",
      "Wales - Neath Port Talbot",
      "Wales - Newport",
      "Wales - Pembrokeshire",
      "Wales - Powys",
      "Wales - Rhondda Cynon Taf",
      "Wales - Torfaen",
      "Wales - Vale of Glamorgan",
      "Wales - Wrexham",
    ],
    "United States": ["North America"],
    Uruguay: ["South America"],
    Uzbekistan: ["Asia"],
    Venezuela: ["South America"],
    Vietnam: ["Asia"],
    Yemen: ["Asia"],
    Zambia: ["Africa"],
    Zimbabwe: ["Africa"],
  };

  const formCardStyle = {
    backgroundColor: "#ffffff",
    padding: "32px",
    borderRadius: "17.6px",
    width: "100%",
    maxWidth: "1500px",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.1)",
    boxSizing: "border-box",
  };

  const boxStyle = {
    backgroundColor: "#ffffff",
    border: "1px solid #dcdcdc",
    borderRadius: "17.6px",
    padding: "20px",
    marginBottom: "16px",
    boxSizing: "border-box",
  };

  const headingStyle = {
    textAlign: "center",
    fontSize: "28px",
    fontWeight: "900",
    marginBottom: "4px",
    background: "linear-gradient(90deg, #2D4CA8 52%, #9F4AFF 60%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  };

  const subheadingStyle = {
    textAlign: "center",
    color: "#063b8a",
    fontWeight: "700",
    fontSize: "14px",
    marginBottom: "24px",
  };

  const sectionTitleStyle = {
    fontSize: "18px",
    fontWeight: "700",
    color: "#052C65",
    marginBottom: "12px",
  };

  const labelStyle = {
    fontWeight: "medium",
    fontSize: "14px",
    color: "#052C65",
    marginBottom: "6px",
    display: "block",
  };

  const inputRowStyle = {
    display: "flex",
    gap: "24px",
    flexWrap: "wrap",
  };

  const inputRowStyleChild = {
    flex: "1",
    minWidth: "200px",
  };

  const phoneInputStyle = {
    width: "100%",
    height: "40px",
    padding: "9px",
    borderRadius: "12px",
    border: "1px solid #ccc",
    fontSize: "14px",
    boxSizing: "border-box",
    paddingLeft: "52px",
  };

  const inputStyle = {
    width: "100%",
    height: "40px",
    padding: "9px",
    borderRadius: "12px",
    border: "1px solid #ccc",
    fontSize: "14px",
    boxSizing: "border-box",
  };

  const errorInputStyle = {
    ...inputStyle,
    border: "1px solid #d32f2f",
  };

  const selectStyle = {
    ...inputStyle,
  };

  const checkboxContainerStyle = {
    display: "flex",
    gap: "10px",
    fontSize: "13px",
    color: "#000080",
    marginBottom: "16px",
  };

  const buttonStyle = {
    width: "40%",
    padding: "12px",
    backgroundColor: "#000080",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    fontWeight: "500",
    cursor: "pointer",
    fontSize: "16px",
    position: "relative",
    zIndex: 10,  
  };

  const linkStyle = {
    color: "#4f46e5",
    textDecoration: "underline",
  };

  const loginLinkContainerStyle = {
    textAlign: "center",
    marginTop: "16px",
    fontSize: "16px",
    color: "#052C65",
  };

  const loginLinkStyle = {
    color: "#052C65",
    fontWeight: "700",
    textDecoration: "none",
    cursor: "pointer",
  };

  const errorStyle = {
    color: "#d32f2f",
    fontSize: "12px",
    marginTop: "4px",
    display: "block",
  };

  // Show referral banner when referral code exists
  const referralBannerStyle = {
    backgroundColor: "rgba(0, 48, 135, 0.1)",
    border: "1px solid rgba(0, 48, 135, 0.2)",
    borderRadius: "12px",
    padding: "15px",
    marginBottom: "20px",
    textAlign: "center",
  };

  const referralTextStyle = {
    color: "#003087",
    fontWeight: "600",
    fontSize: "14px",
    margin: "0",
  };

  // Photo upload styles
  const photoUploadContainerStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: "16px",
  };

  const photoPreviewStyle = {
    width: "120px",
    height: "120px",
    borderRadius: "50%",
    objectFit: "cover",
    border: "2px solid #dcdcdc",
    marginBottom: "12px",
  };

  const photoUploadButtonStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 20px",
    backgroundColor: "#f8f9fa",
    border: "2px dashed #dcdcdc",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    color: "#052C65",
    transition: "all 0.3s ease",
  };

  const photoUploadButtonHoverStyle = {
    backgroundColor: "#e9ecef",
    borderColor: "#052C65",
  };

  const removePhotoButtonStyle = {
    position: "absolute",
    top: "-5px",
    right: "-5px",
    backgroundColor: "#ff4444",
    color: "white",
    border: "none",
    borderRadius: "50%",
    width: "24px",
    height: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    fontSize: "12px",
  };

  const photoPreviewContainerStyle = {
    position: "relative",
    display: "inline-block",
  };

  const handleRegionChange = (e) => {
    const { name, value } = e.target;

    if (name === "country") {
      setFormData((prev) => ({
        ...prev,
        country: value,
        region: regionOptions[value]?.[0] || "",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Enhanced photo upload handler with better validation
  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Reset previous errors
    setPhotoError("");
    setErrors((prev) => ({ ...prev, photo: "" }));

    // Validate file type with more specific checks
    const validTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
      "image/avif",
    ];
    if (!validTypes.includes(file.type)) {
      const errorMsg =
        "Please select a valid image file (JPEG, JPG, PNG, GIF, WEBP, AVIF)";
      setPhotoError(errorMsg);
      setErrors((prev) => ({ ...prev, photo: errorMsg }));
      return;
    }

    // Validate file extension as additional check
    const validExtensions = [".jpeg", ".jpg", ".png", ".gif", ".webp", ".avif"];
    const fileExtension = "." + file.name.split(".").pop().toLowerCase();
    if (!validExtensions.includes(fileExtension)) {
      const errorMsg =
        "Please select a valid image file (JPEG, JPG, PNG, GIF, WEBP, AVIF)";
      setPhotoError(errorMsg);
      setErrors((prev) => ({ ...prev, photo: errorMsg }));
      return;
    }

    // Validate file size (2MB max)
    const maxSize = 2 * 1024 * 1024; // 2MB in bytes
    if (file.size > maxSize) {
      const errorMsg = "Image size should be less than 2MB";
      setPhotoError(errorMsg);
      setErrors((prev) => ({ ...prev, photo: errorMsg }));
      return;
    }

    // Additional check: verify it's actually an image by checking if we can create an Image object
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = function () {
      // Image loaded successfully, it's a valid image
      setPhoto(file);

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotoPreview(e.target.result);
      };
      reader.readAsDataURL(file);

      URL.revokeObjectURL(objectUrl);
    };

    img.onerror = function () {
      // Failed to load as image
      const errorMsg = "The selected file is not a valid image";
      setPhotoError(errorMsg);
      setErrors((prev) => ({ ...prev, photo: errorMsg }));
      URL.revokeObjectURL(objectUrl);
    };

    img.src = objectUrl;
  };

  const handleRemovePhoto = () => {
    setPhoto(null);
    setPhotoPreview(null);
    setPhotoError("");
    setErrors((prev) => ({ ...prev, photo: "" }));
    // Clear the file input
    const fileInput = document.getElementById("photo-upload");
    if (fileInput) {
      fileInput.value = "";
    }
  };

  // Validation functions
  const validateFirstName = (value) => {
    if (!value.trim()) return "First name is required";
    if (value.length < 3) return "First name must be at least 3 characters";
    if (value.length > 50) return "First name must not exceed 50 characters";
    return "";
  };

  const validateLastName = (value) => {
    if (!value.trim()) return "Last name is required";
    if (value.length < 2) return "Last name must be at least 2 characters";
    if (value.length > 50) return "Last name must not exceed 50 characters";
    return "";
  };

  const validateEmail = (value) => {
    if (!value.trim()) return "Email is required";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return "Please enter a valid email address";
    if (value.length > 255) return "Email must not exceed 255 characters";
    return "";
  };

  const validatePhone = (value) => {
    if (!value) return "Phone number is required";
    if (value.replace(/\D/g, "").length < 10)
      return "Please enter a valid phone number";
    if (value.length > 20) return "Phone number must not exceed 20 characters";
    return "";
  };

  const validateAddressLine1 = (value) => {
    if (!value.trim()) return "Address line 1 is required";
    if (value.length > 100)
      return "Address line 1 must not exceed 100 characters";
    return "";
  };

  const validateAddressLine2 = (value) => {
    if (value.length === 0) return "";
    if (value.length > 100)
      return "Address line 2 must not exceed 100 characters";
    return "";
  };

  const validateAddressLine3 = (value) => {
    if (value.length === 0) return "";
    if (value.length > 100)
      return "Address line 3 must not exceed 100 characters";
    return "";
  };

  const validateCity = (value) => {
    if (!value.trim()) return "City is required";
    if (value.length > 100) return "City must not exceed 100 characters";
    return "";
  };

  const validatePostalCode = (value) => {
    if (!value.trim()) return "Postal code is required";
    if (!/^[A-Za-z0-9\s\-]+$/.test(value))
      return "Please enter a valid postal code";
    if (value.length > 20) return "Postal code must not exceed 20 characters";
    return "";
  };

  const validatePassword = (value) => {
    if (!value) return "Password is required";
    if (value.length < 8) return "Password must be at least 8 characters long";
    if (value.length > 50) return "Password must not exceed 50 characters";

    const strongRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/;
    if (!strongRegex.test(value)) {
      return "Password should include uppercase, lowercase, number and special character";
    }

    return "";
  };

  const validatePasswordConfirmation = (value) => {
    if (!value) return "Please confirm your password";
    if (value !== formData.password) return "Passwords do not match";
    return "";
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Validate field immediately after change if it's been touched before
    if (touched[name]) {
      let error = "";

      switch (name) {
        case "first_name":
          error = validateFirstName(value);
          break;
        case "last_name":
          error = validateLastName(value);
          break;
        case "email":
          error = validateEmail(value);
          break;
        case "password":
          error = validatePassword(value);
          break;
        case "password_confirmation":
          error = validatePasswordConfirmation(value);
          break;
        case "address_line1":
          error = validateAddressLine1(value);
          break;
        case "address_line2":
          error = validateAddressLine2(value);
          break;
        case "address_line3":
          error = validateAddressLine3(value);
          break;
        case "city":
          error = validateCity(value);
          break;
        case "postcode": // ADDED
          error = validatePostalCode(value);
          break;
        default:
          break;
      }

      setErrors((prev) => ({
        ...prev,
        [name]: error,
      }));
    }

    // Clear messages when user starts typing
    if (message.text) {
      setMessage({ text: "", type: "" });
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;

    // Mark field as touched
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    // Validate the field
    let error = "";

    switch (name) {
      case "first_name":
        error = validateFirstName(value);
        break;
      case "last_name":
        error = validateLastName(value);
        break;
      case "email":
        error = validateEmail(value);
        break;
      case "password":
        error = validatePassword(value);
        break;
      case "password_confirmation":
        error = validatePasswordConfirmation(value);
        break;
      case "address_line1":
        error = validateAddressLine1(value);
        break;
      case "address_line2":
        error = validateAddressLine2(value);
        break;
      case "address_line3":
        error = validateAddressLine3(value);
        break;
      case "city":
        error = validateCity(value);
        break;
      case "postcode": // ADDED
        error = validatePostalCode(value);
        break;
      default:
        break;
    }

    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  const handlePhoneChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      phone_number: value,
    }));

    // Validate phone if it's been touched before
    if (touched.phone_number) {
      const error = validatePhone(value);
      setErrors((prev) => ({
        ...prev,
        phone_number: error,
      }));
    }

    // Clear messages when user starts typing
    if (message.text) {
      setMessage({ text: "", type: "" });
    }
  };

  const handlePhoneBlur = () => {
    // Mark phone as touched
    setTouched((prev) => ({
      ...prev,
      phone_number: true,
    }));

    // Validate phone
    const error = validatePhone(formData.phone_number);
    setErrors((prev) => ({
      ...prev,
      phone_number: error,
    }));
  };

  const validateForm = () => {
    // Validate all fields
    const newErrors = {
      first_name: validateFirstName(formData.first_name),
      last_name: validateLastName(formData.last_name),
      email: validateEmail(formData.email),
      phone_number: validatePhone(formData.phone_number),
      password: validatePassword(formData.password),
      password_confirmation: validatePasswordConfirmation(
        formData.password_confirmation,
      ),
      address_line1: validateAddressLine1(formData.address_line1),
      address_line2: validateAddressLine2(formData.address_line2),
      address_line3: validateAddressLine3(formData.address_line3),
      city: validateCity(formData.city),
      postcode: validatePostalCode(formData.postcode), // ADDED
      photo: photo ? "" : "", // Photo is optional, so no error if not provided
    };

    setErrors(newErrors);

    // Mark all fields as touched to show errors
    setTouched({
      first_name: true,
      last_name: true,
      email: true,
      phone_number: true,
      password: true,
      password_confirmation: true,
      address_line1: true,
      address_line2: true,
      address_line3: true,
      city: true,
      postcode: true, // ADDED
    });

    // Check if there are any errors
    const hasErrors = Object.values(newErrors).some((error) => error !== "");

    // Check if terms are agreed to
    if (!agreedToTerms) {
      setMessage({
        text: "Please accept our Privacy Policy and Terms & Conditions",
        type: "error",
      });
      return false;
    }

    if (!agreedToRetailAgreement) {
      setMessage({
        text: "Please accept the RetailBusiness Advertising Agent Agreement",
        type: "error",
      });
      return false;
    }

    return !hasErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check reCAPTCHA
    if (!captchaToken) {
      setMessage({
        text: "Please verify that you are not a robot.",
        type: "error",
      });
      return;
    }

    if (!validateForm()) return;

    setIsLoading(true);
    setMessage({ text: "", type: "" });

    try {
      // Store password temporarily for auto-login after verification
      localStorage.setItem("tempPassword", formData.password);

      // Build API URL with referral code if present
      let apiUrl = `${API_URL}/api/agent/register`;

      // Create URLSearchParams to handle query parameters properly
      const queryParams = new URLSearchParams();
      if (referralCode) {
        queryParams.append("ref", referralCode);
      }

      // Append query string if we have parameters
      const queryString = queryParams.toString();
      if (queryString) {
        apiUrl += `?${queryString}`;
      }

      // Create FormData object for file upload
      const submitData = new FormData();

      // Append all form data
      Object.keys(formData).forEach((key) => {
        if (key !== "showPassword" && key !== "showConfirmPassword") {
          submitData.append(key, formData[key]);
        }
      });

      // Append photo if exists and is valid
      if (photo) {
        // Double-check the file type before sending
        const validTypes = [
          "image/jpeg",
          "image/jpg",
          "image/png",
          "image/gif",
          "image/webp",
          "image/avif",
        ];
        if (!validTypes.includes(photo.type)) {
          throw new Error("Invalid image file type");
        }
        submitData.append("photo", photo);
      }

      // Append captcha token
      submitData.append("g-recaptcha-response", captchaToken);

      console.log("Submitting with referral code:", referralCode);
      console.log("API URL:", apiUrl);

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          Accept: "application/json",
          // Don't set Content-Type for FormData, let browser set it with boundary
        },
        body: submitData,
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({
          text: "Account created successfully! Please check your email for verification.",
          type: "success",
        });

        // Store user email for verification page
        localStorage.setItem("verifyEmail", formData.email);

        // Clear referral code from localStorage after successful registration
        localStorage.removeItem("agent_referral_code");

        // Redirect to verification page after a short delay
        setTimeout(() => {
          navigate("/verify-email-a");
        }, 2000);
      } else {
        // Handle validation errors from backend
        if (data.errors) {
          // Handle photo errors specifically
          if (data.errors.photo) {
            setPhotoError(data.errors.photo[0]);
            setErrors((prev) => ({ ...prev, photo: data.errors.photo[0] }));
          }

          // Show the first error message
          const firstErrorKey = Object.keys(data.errors)[0];
          const firstError = data.errors[firstErrorKey][0];
          setMessage({
            text: firstError,
            type: "error",
          });
        } else {
          setMessage({
            text: data.message || "Failed to create account. Please try again.",
            type: "error",
          });
        }

        // Remove temporary password if registration failed
        localStorage.removeItem("tempPassword");
      }
    } catch (error) {
      console.error("Registration error:", error);
      setMessage({
        text:
          error.message ||
          "Network error. Please check your connection and try again.",
        type: "error",
      });

      // Remove temporary password if registration failed
      localStorage.removeItem("tempPassword");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ ...containerStyle, minHeight: "auto" }}>
      <Header />

      <div style={formWrapperStyle}>
        <div
          className="form-card p-3 m-3 pt-4 h-auto"
          style={{ ...formCardStyle, minHeight: "auto", maxWidth: "900px" }}
        >
          <h2 style={headingStyle}>Create Account</h2>
          <p style={subheadingStyle}>
            Enter your details below to set up your account
          </p>

          {/* Show referral banner if referral code exists */}
          {/* {referralCode && (
            <div style={referralBannerStyle}>
              <p style={referralTextStyle}>
                ✨ You're signing up with a referral link! Referral Code: <strong>{referralCode}</strong>
              </p>
            </div>
          )} */}

          {/* Message display */}
          {message.text && (
            <div
              style={{
                textAlign: "center",
                padding: "10px",
                borderRadius: "8px",
                margin: "20px 0",
                fontWeight: "500",
                backgroundColor:
                  message.type === "error" ? "#ffebee" : "#e8f5e9",
                color: message.type === "error" ? "#c62828" : "#2e7d32",
              }}
            >
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Profile Photo Box */}
            <div style={boxStyle} className="mb-2 p-3">
              <div style={sectionTitleStyle} className="mb-0">
                Profile Photo
              </div>
              <div style={photoUploadContainerStyle}>
                {photoPreview ? (
                  <div style={photoPreviewContainerStyle}>
                    <img
                      src={photoPreview}
                      alt="Profile preview"
                      style={photoPreviewStyle}
                    />
                    <button
                      type="button"
                      style={removePhotoButtonStyle}
                      onClick={handleRemovePhoto}
                      disabled={isLoading}
                    >
                      <FaTimes />
                    </button>
                  </div>
                ) : (
                  <div
                    style={{
                      width: "120px",
                      height: "120px",
                      borderRadius: "50%",
                      backgroundColor: "#f8f9fa",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "2px dashed #dcdcdc",
                      marginBottom: "12px",
                      color: "#6c757d",
                    }}
                  >
                    <FaCamera size={24} />
                  </div>
                )}

                <input
                  type="file"
                  id="photo-upload"
                  accept=".jpg,.jpeg,.png,.gif,image/jpeg,image/jpg,image/png,image/gif,image/webp,image/avif"
                  onChange={handlePhotoUpload}
                  style={{ display: "none" }}
                  disabled={isLoading}
                />

                <label
                  htmlFor="photo-upload"
                  style={photoUploadButtonStyle}
                  onMouseOver={(e) =>
                    Object.assign(e.target.style, photoUploadButtonHoverStyle)
                  }
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = "#f8f9fa";
                    e.target.style.borderColor = "#dcdcdc";
                  }}
                >
                  <FaCamera />
                  {photoPreview ? "Change Photo" : "Upload Photo"}
                </label>

                {(photoError || errors.photo) && (
                  <span style={errorStyle}>{photoError || errors.photo}</span>
                )}

                <p
                  style={{
                    fontSize: "12px",
                    color: "#6c757d",
                    marginTop: "8px",
                    textAlign: "center",
                  }}
                >
                  Supported formats: JPEG, JPG, PNG, GIF
                  <br />
                  Max size: 2MB
                </p>
              </div>
            </div>

            {/* Rest of the form remains the same */}
            {/* Name Box */}
            <div style={boxStyle} className="mb-2 p-3">
              <div style={sectionTitleStyle} className="mb-0">
                Name
              </div>
              <div style={inputRowStyle} className="gap-1 gap-md-3">
                <div style={inputRowStyleChild}>
                  <label style={labelStyle}>First name</label>
                  <input
                    type="text"
                    name="first_name"
                    placeholder="John"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    style={errors.first_name ? errorInputStyle : inputStyle}
                    disabled={isLoading}
                  />
                  {errors.first_name && (
                    <span style={errorStyle}>{errors.first_name}</span>
                  )}
                </div>
                <div style={inputRowStyleChild}>
                  <label style={labelStyle}>Last name</label>
                  <input
                    type="text"
                    name="last_name"
                    placeholder="Wick"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    style={errors.last_name ? errorInputStyle : inputStyle}
                    disabled={isLoading}
                  />
                  {errors.last_name && (
                    <span style={errorStyle}>{errors.last_name}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Contact Box */}
            <div style={boxStyle} className="mb-2 p-3">
              <div style={sectionTitleStyle} className="mb-0">
                Contact
              </div>
              <div style={inputRowStyle} className="gap-1 gap-md-3">
                <div style={inputRowStyleChild}>
                  <label style={labelStyle}>Email</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="johnwick01@gmail.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    style={errors.email ? errorInputStyle : inputStyle}
                    disabled={isLoading}
                  />
                  {errors.email && (
                    <span style={errorStyle}>{errors.email}</span>
                  )}
                </div>
                <div style={inputRowStyleChild}>
                  <label style={labelStyle}>Phone number</label>
                  <PhoneInput
                    country={"gb"}
                    value={formData.phone_number}
                    onChange={handlePhoneChange}
                    onBlur={handlePhoneBlur}
                    inputStyle={
                      errors.phone_number
                        ? { ...phoneInputStyle, border: "1px solid #d32f2f" }
                        : phoneInputStyle
                    }
                    buttonStyle={{ borderRadius: "12px 0 0 12px" }}
                    disabled={isLoading}
                  />
                  {errors.phone_number && (
                    <span style={errorStyle}>{errors.phone_number}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Address Box */}
            <div style={boxStyle} className="mb-2 p-3">
              <div style={sectionTitleStyle} className="mb-0">
                Address
              </div>
              <div style={inputRowStyle} className="gap-1 gap-md-3"></div>

              {/* UPDATED: Address Line Fields with Post Code */}
              <div style={{ marginTop: "16px" }}>
                {/* Row 1: Address Line 1 and Address Line 2 */}
                <div style={inputRowStyle} className="gap-1 gap-md-3">
                  <div style={inputRowStyleChild}>
                    <label style={labelStyle}>
                      Address Line 1 <span style={{ color: "red" }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="address_line1"
                      placeholder="Street address, P.O. box, company name"
                      value={formData.address_line1}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      style={
                        errors.address_line1 ? errorInputStyle : inputStyle
                      }
                      disabled={isLoading}
                    />
                    {errors.address_line1 && (
                      <span style={errorStyle}>{errors.address_line1}</span>
                    )}
                  </div>
                  <div style={inputRowStyleChild}>
                    <label style={labelStyle}>Address Line 2</label>
                    <input
                      type="text"
                      name="address_line2"
                      placeholder="Apartment, suite, unit, building, floor, etc."
                      value={formData.address_line2}
                      onChange={handleInputChange}
                      // style={inputStyle}
                      style={
                        errors.address_line2 ? errorInputStyle : inputStyle
                      }
                      disabled={isLoading}
                    />
                    {errors.address_line2 && (
                      <span style={errorStyle}>{errors.address_line2}</span>
                    )}
                  </div>
                </div>

                {/* Row 2: Address Line 3 - full width */}
                <div
                  className="gap-1 gap-md-3"
                  style={{ ...inputRowStyle, marginTop: "16px" }}
                >
                  <div
                    style={{
                      ...inputRowStyleChild,
                      flex: "2",
                      minWidth: "100%",
                    }}
                  >
                    <label style={labelStyle}>Address Line 3</label>
                    <input
                      type="text"
                      name="address_line3"
                      placeholder="Additional address information"
                      value={formData.address_line3}
                      onChange={handleInputChange}
                      // style={inputStyle}
                      style={
                        errors.address_line3 ? errorInputStyle : inputStyle
                      }
                      disabled={isLoading}
                    />
                    {errors.address_line3 && (
                      <span style={errorStyle}>{errors.address_line3}</span>
                    )}
                  </div>
                </div>

                {/* Row 3: City and Region */}
                <div
                  className="gap-1 gap-md-3"
                  style={{ ...inputRowStyle, marginTop: "16px" }}
                >
                  <div style={inputRowStyleChild}>
                    <label style={labelStyle}>
                      City <span style={{ color: "red" }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="city"
                      placeholder="City"
                      value={formData.city}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      style={errors.city ? errorInputStyle : inputStyle}
                      disabled={isLoading}
                    />
                    {errors.city && (
                      <span style={errorStyle}>{errors.city}</span>
                    )}
                  </div>
                  <div style={inputRowStyleChild}>
                    <label style={labelStyle}>Region</label>
                    <select
                      name="region"
                      value={formData.region}
                      onChange={handleInputChange}
                      style={selectStyle}
                      disabled={isLoading}
                    >
                      {(regionOptions[formData.country] || []).map((region) => (
                        <option key={region} value={region}>
                          {region}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Row 4: Post Code and Country */}
                <div
                  className="gap-1 gap-md-3"
                  style={{ ...inputRowStyle, marginTop: "16px" }}
                >
                  <div style={inputRowStyleChild}>
                    <label style={labelStyle}>
                      Post Code <span style={{ color: "red" }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="postcode"
                      placeholder="Postal/Zip code"
                      value={formData.postcode}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      style={errors.postcode ? errorInputStyle : inputStyle}
                      disabled={isLoading}
                    />
                    {errors.postcode && (
                      <span style={errorStyle}>{errors.postcode}</span>
                    )}
                  </div>
                  <div style={inputRowStyleChild}>
                    <label style={labelStyle}>Country</label>
                    <select
                      name="country"
                      value={formData.country}
                      onChange={handleRegionChange}
                      style={selectStyle}
                      disabled={isLoading}
                    >
                      {Object.keys(regionOptions).map((country) => (
                        <option key={country} value={country}>
                          {country}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Security Box */}
            <div style={boxStyle} className="mb-2 p-3">
              <div style={sectionTitleStyle} className="mb-0">
                Security
              </div>
              <div style={inputRowStyle} className="gap-1 gap-md-3">
                <div style={inputRowStyleChild}>
                  <label style={labelStyle}>Password</label>
                  <div style={{ position: "relative" }}>
                    <input
                      type={formData.showPassword ? "text" : "password"}
                      name="password"
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      style={errors.password ? errorInputStyle : inputStyle}
                      disabled={isLoading}
                    />
                    <span
                      style={{
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                      }}
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          showPassword: !prev.showPassword,
                        }))
                      }
                    >
                      {formData.showPassword ? <FaEye /> : <FaEyeSlash />}
                    </span>
                  </div>
                  {errors.password && (
                    <span style={errorStyle}>{errors.password}</span>
                  )}
                </div>
                <div style={inputRowStyleChild}>
                  <label style={labelStyle}>Confirm Password</label>
                  <div style={{ position: "relative" }}>
                    <input
                      type={formData.showConfirmPassword ? "text" : "password"}
                      name="password_confirmation"
                      placeholder="Re-enter your password"
                      value={formData.password_confirmation}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      style={
                        errors.password_confirmation
                          ? errorInputStyle
                          : inputStyle
                      }
                      disabled={isLoading}
                    />
                    <span
                      style={{
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                      }}
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          showConfirmPassword: !prev.showConfirmPassword,
                        }))
                      }
                    >
                      {formData.showConfirmPassword ? (
                        <FaEye />
                      ) : (
                        <FaEyeSlash />
                      )}
                    </span>
                  </div>
                  {errors.password_confirmation && (
                    <span style={errorStyle}>
                      {errors.password_confirmation}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Checkbox */}
            <div
              style={checkboxContainerStyle}
              className="justify-content-center"
            >
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                disabled={isLoading}
              />
              <span style={{ color: "#000000" }}>
                By clicking continue you accept our{" "}
                <a
                  href="/privacy-policy"
                  target="_blank"
                  style={{
                    ...linkStyle,
                    color: "#052C65",
                    textDecoration: "none",
                    fontWeight: "bold",
                  }}
                  onMouseOver={(e) => (e.target.style.textDecoration = "none")}
                  onMouseOut={(e) => (e.target.style.textDecoration = "none")}
                >
                  Privacy Policy
                </a>{" "}
                and{" "}
                <a
                  href="/terms-conditions"
                  target="_blank"
                  style={{
                    ...linkStyle,
                    color: "#052C65",
                    textDecoration: "none",
                    fontWeight: "bold",
                  }}
                  onMouseOver={(e) => (e.target.style.textDecoration = "none")}
                  onMouseOut={(e) => (e.target.style.textDecoration = "none")}
                >
                  Terms & Conditions
                </a>
              </span>
            </div>

            <div
              style={checkboxContainerStyle}
              className="justify-content-center"
            >
              <input
                type="checkbox"
                checked={agreedToRetailAgreement}
                onChange={(e) => setAgreedToRetailAgreement(e.target.checked)}
                disabled={isLoading}
              />
              <span style={{ color: "#000000" }}>
                I have read and agree to the{" "}
                <a
                  href="/retail-agent-agreement"
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    ...linkStyle,
                    color: "#052C65",
                    textDecoration: "none",
                    fontWeight: "bold",
                  }}
                  onMouseOver={(e) => (e.target.style.textDecoration = "none")}
                  onMouseOut={(e) => (e.target.style.textDecoration = "none")}
                >
                  RetailBusiness Advertising Agent Agreement
                </a>
                .
              </span>
            </div>

            {/* reCAPTCHA */}
            <div
              style={{
                marginBottom: "16px",
                display: "flex",
                alignItems: "center",
                flexDirection: "column",
              }}
            >
              <ReCAPTCHA
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY} // replace with your actual site key
                onChange={onCaptchaChange}
              />
              {/* { !captchaToken && message.type === "error" && message.text.includes("robot") && (
                <span style={{ ...errorStyle, display: "block", marginTop: "8px" }}>
                  {message.text}
                </span>
              )} */}
            </div>

            <div className="d-flex justify-content-center">
              {/* Button */}
              <button
                type="submit"
                style={{
                  ...buttonStyle,
                  opacity: isLoading ? 0.7 : 1,
                  cursor: isLoading ? "not-allowed" : "pointer",
                }}
                onMouseOver={(e) =>
                  !isLoading && (e.target.style.backgroundColor = "#1a1aa0")
                }
                onMouseOut={(e) =>
                  !isLoading && (e.target.style.backgroundColor = "#000080")
                }
                disabled={isLoading}
              >
                {isLoading ? "Creating Account..." : "Create Account"}
              </button>
            </div>
          </form>

          {/* Login link */}
          <div style={loginLinkContainerStyle}>
            <span
              style={{
                color: "#444444",
                fontWeight: "semibold",
                fontSize: "16px",
              }}
            >
              Already have an account?{" "}
            </span>
            <a
              href="/work-with-tcuk"
              style={{ ...loginLinkStyle, textDecoration: "none" }}
              onMouseOver={(e) => (e.target.style.textDecoration = "none")}
              onMouseOut={(e) => (e.target.style.textDecoration = "none")}
            >
              Login here
            </a>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CreateRetailAgent;
